import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

const ThirteenthMonthPayslipDialog = ({ open, onClose, employee, year, store }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payslipData, setPayslipData] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open && employee) {
      fetchPayslipData();
    }
  }, [open, employee]);

  const fetchPayslipData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 600));
      console.log("HERE")
      // Mock payslip data
      const mockData = {
        employee: {
          name: employee.employeeName,
          id: employee.employeeId
        },
        company: {
          name: '7-STAR MANPOWER SERVICES OF THE PHILIPPINES CORPORATION',
          branch: store,
          periodCovered: `OCTOBER 16-31, 2024 TO OCTOBER 1-15, ${year}`
        },
        period: {
          year: year,
          dateCovered: `January 1, ${year} - December 31, ${year}`
        },
        cutoffs: {
          OCT1631: employee.OCT1631,
          NOV115: employee.NOV115,
          NOV1630: employee.NOV1630,
          DEC115: employee.DEC115,
          DEC1631: employee.DEC1631,
          JAN115: employee.JAN115,
          JAN1631: employee.JAN1631,
          FEB115: employee.FEB115,
          FEB1628: employee.FEB1628
        },
        computation: {
          totalBasicPay: employee.totalBasicPay,
          thirteenthMonthPay: employee.thirteenthMonthPay,
          taxExemption: 90000, // 90k tax exemption
          taxableAmount: Math.max(0, employee.totalBasicPay - 90000),
          withholdingTax: Math.max(0, employee.totalBasicPay - 90000) * 0.05, // Simplified tax calculation
          netPay: employee.thirteenthMonthPay - (Math.max(0, employee.totalBasicPay - 90000) * 0.05)
        },
        dateGenerated: new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: '2-digit'
        })
      };

      setPayslipData(mockData);
    } catch (err) {
      console.error('Error fetching payslip data:', err);
      setError('Failed to load payslip data');
      enqueueSnackbar('Failed to load payslip data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!payslipData) return;

    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownload = () => {
    handlePrint(); // For now, download also uses print functionality
    enqueueSnackbar('Generating PDF...', { variant: 'info' });
  };

  const generatePrintContent = () => {
    if (!payslipData) return '';

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>13th Month Pay - ${payslipData.employee.name}</title>
          <style>
            @page {
              size: A4;
              margin: 0.5in;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              background: white;
              color: #333;
            }
            .payslip-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
            }
            .header {
              background: linear-gradient(135deg, #12C1DC, #0FABDB);
              color: white;
              padding: 20px;
              text-align: center;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              font-weight: bold;
            }
            .header h2 {
              margin: 10px 0 0 0;
              font-size: 18px;
              font-weight: normal;
            }
            .employee-info {
              padding: 20px;
              background: #f8f9fa;
              border-bottom: 2px solid #e5e7eb;
            }
            .employee-info h2 {
              margin: 0 0 10px 0;
              color: #2c3e50;
              font-size: 20px;
            }
            .employee-info p {
              margin: 5px 0;
              color: #6b7280;
            }
            .payslip-content {
              padding: 20px;
            }
            .computation-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            .computation-table td {
              padding: 12px;
              border-bottom: 1px solid #e5e7eb;
            }
            .computation-table tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            .computation-table td:first-child {
              font-weight: 600;
              color: #374151;
            }
            .computation-table td:last-child {
              text-align: right;
              font-weight: 600;
            }
            .total-row {
              border-top: 3px solid #12C1DC !important;
              background-color: #e8f7fa !important;
              font-size: 18px;
            }
            .total-row td {
              color: #0fb8d0;
              font-weight: bold;
            }
            .summary {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              margin: 20px 0;
              border: 2px solid #12C1DC;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #e5e7eb;
            }
            .summary-row:last-child {
              border-bottom: none;
              font-weight: bold;
              font-size: 20px;
              color: #10b981;
            }
            .acknowledgement {
              margin-top: 30px;
              text-align: center;
            }
            .acknowledgement h4 {
              margin: 0 0 10px 0;
              color: #2c3e50;
            }
            .signature-line {
              border-bottom: 1px solid #333;
              width: 200px;
              margin: 20px auto 5px auto;
            }
            .signature-label {
              font-size: 12px;
              color: #6b7280;
            }
            .footer {
              margin-top: 30px;
              padding: 15px;
              background: #f8f9fa;
              border-top: 2px solid #e5e7eb;
              text-align: center;
              font-size: 12px;
              color: #6b7280;
            }
            @media print {
              .no-print { display: none !important; }
              body { margin: 0; }
              .payslip-container { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="payslip-container">
            <div class="header">
              <h1>${payslipData.company.name}</h1>
              <h2>13th Month Pay Slip</h2>
            </div>
            
            <div class="employee-info">
              <p><strong>BRANCH:</strong> ${payslipData.company.branch}</p>
              <p><strong>PERIOD COVERED:</strong> ${payslipData.company.periodCovered}</p>
            </div>

            <div class="payslip-content">
              <h3 style="color: #2c3e50; margin-bottom: 15px;">CUTOFF PERIOD BREAKDOWN</h3>
              <table class="computation-table">
                <thead>
                  <tr style="background: #12C1DC; color: white;">
                    <th style="padding: 10px; text-align: left; border: 1px solid #0fb8d0;">NAME</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">OCT1631</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">NOV115</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">NOV1630</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">DEC115</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">DEC1631</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">JAN115</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">JAN1631</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">FEB115</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">FEB1628</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">TOTAL BASIC PAY</th>
                    <th style="padding: 10px; text-align: right; border: 1px solid #0fb8d0;">TOTAL 13TH MP</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; font-weight: 600;">${payslipData.employee.name}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right;">${payslipData.cutoffs.OCT1631.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right;">${payslipData.cutoffs.NOV115.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right;">${payslipData.cutoffs.NOV1630.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right;">${payslipData.cutoffs.DEC115.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right;">${payslipData.cutoffs.DEC1631.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right;">${payslipData.cutoffs.JAN115.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right;">${payslipData.cutoffs.JAN1631.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right;">${payslipData.cutoffs.FEB115.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right;">${payslipData.cutoffs.FEB1628.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right; font-weight: 700; color: #0fb8d0;">${payslipData.computation.totalBasicPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 10px; border: 1px solid #e5e7eb; text-align: right; font-weight: 700; color: #10b981;">${payslipData.computation.thirteenthMonthPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>

              <h3 style="color: #2c3e50; margin: 30px 0 15px 0;">TAX COMPUTATION</h3>
              <table class="computation-table">
                <tr style="border-top: 2px solid #12C1DC;">
                  <td>TOTAL BASIC PAY:</td>
                  <td>₱${payslipData.computation.totalBasicPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                <tr>
                  <td>LESS: TAX EXEMPTION:</td>
                  <td>₱${payslipData.computation.taxExemption.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                <tr>
                  <td>TAXABLE AMOUNT:</td>
                  <td>₱${payslipData.computation.taxableAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
                <tr>
                  <td>WITHHOLDING TAX:</td>
                  <td>₱${payslipData.computation.withholdingTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              </table>

              <div class="summary">
                <div class="summary-row">
                  <span>TOTAL BASIC PAY:</span>
                  <span>₱${payslipData.computation.totalBasicPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div class="summary-row">
                  <span>TOTAL 13TH MONTH PAY:</span>
                  <span>₱${payslipData.computation.thirteenthMonthPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div class="summary-row">
                  <span>LESS: WITHHOLDING TAX:</span>
                  <span>₱${payslipData.computation.withholdingTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div class="summary-row">
                  <span>NET 13TH MONTH PAY:</span>
                  <span>₱${payslipData.computation.netPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div class="acknowledgement">
                <h4>ACKNOWLEDGEMENT</h4>
                <p>I HEREBY ACKNOWLEDGE RECEIPT OF MY 13TH MONTH PAY FOR THE YEAR ${payslipData.period.year}.</p>
                <div class="signature-line"></div>
                <p class="signature-label">Employee Signature with Date</p>
              </div>
            </div>

            <div class="footer">
              <p>Generated on: ${payslipData.dateGenerated}</p>
              <p>This is a computer-generated document and does not require a signature.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #12C1DC 0%, #0fb8d0 100%)',
        color: 'white',
        py: 2
      }}>
        <Typography variant="h6" fontWeight="bold">
          13th Month Pay Slip
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            gap: 2
          }}>
            <CircularProgress size={40} sx={{ color: '#12C1DC' }} />
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Loading payslip data...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : payslipData ? (
          <Box sx={{ p: 3 }}>
            {/* Company Header */}
            <Box sx={{
              textAlign: 'center',
              mb: 3,
              pb: 2,
              borderBottom: '2px solid #e5e7eb'
            }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#2c3e50' }}>
                {payslipData.company.name}
              </Typography>
              <Typography variant="h6" sx={{ color: '#6b7280', mt: 1 }}>
                13th Month Pay Slip
              </Typography>
            </Box>

            {/* Employee Information */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', border: '1px solid #e5e7eb' }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>BRANCH:</strong> {payslipData.company.branch}
              </Typography>
              <Typography variant="body2">
                <strong>PERIOD COVERED:</strong> {payslipData.company.periodCovered}
              </Typography>
            </Paper>

            {/* Cutoff Period Breakdown */}
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#2c3e50', mb: 2 }}>
              CUTOFF PERIOD BREAKDOWN
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, border: '1px solid #e5e7eb', overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#12C1DC' }}>
                    <TableCell sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      NAME
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      OCT1631
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      NOV115
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      NOV1630
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      DEC115
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      DEC1631
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      JAN115
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      JAN1631
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      FEB115
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      FEB1628
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      TOTAL BASIC PAY
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'white', border: '1px solid #0fb8d0' }}>
                      TOTAL 13TH MP
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, border: '1px solid #e5e7eb' }}>
                      {payslipData.employee.name}
                    </TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #e5e7eb' }}>
                      {payslipData.cutoffs.OCT1631.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #e5e7eb' }}>
                      {payslipData.cutoffs.NOV115.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #e5e7eb' }}>
                      {payslipData.cutoffs.NOV1630.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #e5e7eb' }}>
                      {payslipData.cutoffs.DEC115.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #e5e7eb' }}>
                      {payslipData.cutoffs.DEC1631.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #e5e7eb' }}>
                      {payslipData.cutoffs.JAN115.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #e5e7eb' }}>
                      {payslipData.cutoffs.JAN1631.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #e5e7eb' }}>
                      {payslipData.cutoffs.FEB115.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #e5e7eb' }}>
                      {payslipData.cutoffs.FEB1628.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#0fb8d0', border: '1px solid #e5e7eb' }}>
                      {payslipData.computation.totalBasicPay.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#10b981', border: '1px solid #e5e7eb' }}>
                      {payslipData.computation.thirteenthMonthPay.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Tax Computation */}
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#2c3e50', mb: 2 }}>
              TAX COMPUTATION
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, border: '1px solid #e5e7eb' }}>
              <Table size="small">
                <TableBody>
                  <TableRow sx={{ borderTop: '2px solid #12C1DC' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      TOTAL BASIC PAY:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      ₱{payslipData.computation.totalBasicPay.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:nth-of-type(even)': { bgcolor: '#f8f9fa' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      LESS: TAX EXEMPTION:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      ₱{payslipData.computation.taxExemption.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:nth-of-type(even)': { bgcolor: '#f8f9fa' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      TAXABLE AMOUNT:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      ₱{payslipData.computation.taxableAmount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:nth-of-type(even)': { bgcolor: '#f8f9fa' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      WITHHOLDING TAX:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      ₱{payslipData.computation.withholdingTax.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Paper sx={{
              p: 2,
              bgcolor: '#f8f9fa',
              border: '2px solid #12C1DC',
              borderRadius: 2
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: '1px solid #e5e7eb'
              }}>
                <Typography variant="body1" fontWeight={600}>
                  TOTAL BASIC PAY:
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  ₱{payslipData.computation.totalBasicPay.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: '1px solid #e5e7eb'
              }}>
                <Typography variant="body1" fontWeight={600}>
                  TOTAL 13TH MONTH PAY:
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  ₱{payslipData.computation.thirteenthMonthPay.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: '1px solid #e5e7eb'
              }}>
                <Typography variant="body1" fontWeight={600}>
                  LESS: WITHHOLDING TAX:
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  ₱{payslipData.computation.withholdingTax.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 2
              }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#10b981' }}>
                  NET 13TH MONTH PAY:
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#10b981' }}>
                  ₱{payslipData.computation.netPay.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
            </Paper>

            {/* Acknowledgement */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#2c3e50', mb: 1 }}>
                ACKNOWLEDGEMENT
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                I HEREBY ACKNOWLEDGE RECEIPT OF MY 13TH MONTH PAY FOR THE YEAR {payslipData.period.year}.
              </Typography>
              <Box sx={{
                width: 200,
                borderBottom: '1px solid #333',
                margin: '0 auto 5px auto'
              }} />
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                Employee Signature with Date
              </Typography>
            </Box>

            {/* Footer */}
            <Box sx={{
              mt: 3,
              pt: 2,
              borderTop: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                Generated on: {payslipData.dateGenerated}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      {!loading && !error && (
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e5e7eb',
          gap: 1,
          justifyContent: 'center'
        }}>
          <Button
            onClick={handleDownload}
            startIcon={<DownloadIcon />}
            variant="outlined"
            sx={{
              borderColor: '#12C1DC',
              color: '#12C1DC',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#0fb8d0',
                bgcolor: 'rgba(18, 193, 220, 0.1)'
              }
            }}
          >
            Download
          </Button>
          <Button
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            variant="contained"
            sx={{
              bgcolor: '#12C1DC',
              textTransform: 'none',
              '&:hover': { bgcolor: '#0fb8d0' }
            }}
          >
            Print
          </Button>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: '#d1d5db',
              color: '#6b7280',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#9ca3af',
                bgcolor: '#f9fafb'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ThirteenthMonthPayslipDialog;

